import localStyles from "../components/Bonus3Section/styles.module.scss";
import {ProductType} from "../context/Contexts";
import DOMPurify from "dompurify";

declare global {
    interface Window {
        scs_custom_rear_seats?: boolean;
    }
}

//window.scs_biz2 ? "http://localhost:8000" :
export const getApiBase = () => "https://scsbiz3.wearevisum.com";

// Appends scs_part_number_skus=true to an SCS API request URL's query string
// when the window.scs_part_number_skus flag is enabled. Use for GET calls;
// for POST calls with a JSON body use withPartNumberSkuBody instead.
export const withPartNumberSkuQuery = (url: string): string => {
    if (window.scs_part_number_skus !== true) return url;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}scs_part_number_skus=true`;
};

// Merges scs_part_number_skus: true into a POST body when the
// window.scs_part_number_skus flag is enabled.
export const withPartNumberSkuBody = <T extends object>(body: T): T & { scs_part_number_skus?: true } => {
    if (window.scs_part_number_skus !== true) return body;
    return { ...body, scs_part_number_skus: true };
};

// Every dangerouslySetInnerHTML call site should route its value through this
// rather than injecting a string directly — merchant/theme globals (window.scs_*)
// are the only sources today, but nothing enforces that boundary, so the safe
// path needs to be the only path.
export const sanitizeHtml = (html: string | undefined | null): string =>
    DOMPurify.sanitize(html ?? "");

// Single source of truth for "which variant does add-to-cart actually use". Prefers
// biz2/sku-mapping/prekit overrides over the raw selected variant, since bundle/prekit
// flows swap in a different SKU than the one the color/seat pickers point to. Every
// place that needs "the variant we're adding to cart" (the add-to-cart form, the
// payment-terms form) must call this instead of reading currentAppData.selectedId
// directly, or those forms can end up disagreeing on the variant id.
export function getSeatCoverId(currentAppData: any): string | undefined {
    if (window.scs_biz2 && currentAppData?.biz2_coverages) {
        const seat = currentAppData?.seat;
        const color = currentAppData?.color;
        const seatKey = resolveAvailabilitySeatKey(seat, currentAppData.biz2_coverages);
        const variantId = currentAppData.biz2_coverages?.[seatKey]?.[color]?.variant_id;
        if (variantId) {
            return variantId;
        }
    }

    if (window.scs_biz2 && currentAppData?.sku_mapping_availability) {
        const skuMapSeatKey = resolveAvailabilitySeatKey(currentAppData?.seat, currentAppData.sku_mapping_availability);
        const skuMapSeatData = currentAppData.sku_mapping_availability?.[skuMapSeatKey];
        const skuMapColor = currentAppData?.color;
        if (skuMapSeatData && skuMapSeatData[skuMapColor] === true) {
            const skuMapPrekit = Array.isArray(skuMapSeatData.prekit) ? {} : (skuMapSeatData.prekit ?? {});
            const skuMapEntry = skuMapPrekit[skuMapColor];
            if (skuMapEntry?.id) {
                return skuMapEntry.id;
            }
        }
    }

    if (currentAppData && currentAppData?.check) {
        if (
            currentAppData?.check.availability &&
            currentAppData?.check.availability[currentAppData?.seat] &&
            currentAppData?.check.availability[currentAppData?.seat]["prekit"] &&
            currentAppData?.check.availability[currentAppData?.seat]["prekit"][
                currentAppData?.color
                ]
        ) {
            return currentAppData?.check.availability[currentAppData?.seat]["prekit"][
                currentAppData?.color
                ]["id"];
        }
    }

    return currentAppData?.selectedId;
}

// The #payment_terms container (rendered by the theme) wraps a hidden form with
// both <shopify-payment-terms> and its own input[name="id"]. Whenever the widget
// changes the selected variant, both need to be kept in sync with it, or the
// installment estimate / that form's own submission would reference a stale variant.
export const syncPaymentTermsVariantId = (variantId: string | undefined | null): void => {
    if (!variantId || typeof document === "undefined") {
        return;
    }
    const container = document.querySelector('#payment_terms');
    if (!container) {
        return;
    }
    container.querySelector('shopify-payment-terms')?.setAttribute('variant-id', variantId);
    const idInput = container.querySelector<HTMLInputElement>('input[name="id"]');
    if (idInput) {
        idInput.value = variantId;
    }
};

export const slugify = function (str: string) {
    str = str.replace(/^\s+|\s+$/g, ''); // trim leading/trailing white space
    str = ucFirst(str); // convert string to lowercase
    str = str.replace(/[^a-zA-Z0-9 -]/g, '') // remove any non-alphanumeric characters
        .replace(/\s+/g, '-') // replace spaces with hyphens
        .replace(/-+/g, ''); // remove consecutive hyphens
    return str;
}

export const slugifyWithDash = function (str: string) {
    str = str.replace(/^\s+|\s+$/g, ''); // trim leading/trailing white space
    str = ucFirst(str); // convert string to lowercase
    str = str.replace(/&/g, 'and');
    str = str.replace(/[^a-zA-Z0-9 -]/g, '') // remove any non-alphanumeric characters
        .replace(/\s+/g, '-') // replace spaces with hyphens
        .replace(/-+/g, '-'); // remove consecutive hyphens
    return str;
}

export const ucFirst = function (word: string) {
    return word.charAt(0).toUpperCase()
        + word.slice(1);
}

export const getCurrency = function (currencyCode: string) {
    return currencyCode === 'USD' ? '$' : currencyCode;
}

export const getFormattedSeatValue = function (label, price, comparePrice, customCompatibility, currentAppData = null) {
    let subtitle = '';
    let value = label;
    let diff: string = '';
    let diffPercentage: string = '';
    let comparePrice1 = comparePrice;
    if (comparePrice) {
        const comparePriceNum = parseFloat(comparePrice.replace(/[^0-9.]/g, ''));
        const priceNum = parseFloat(price.replace(/[^0-9.]/g, ''));
        diff = '' + formatShopifyMoney(formatMoney(comparePriceNum - priceNum));
        diffPercentage = '' + ((comparePriceNum - priceNum) / comparePriceNum * 100).toFixed(0);
    }
    switch (label) {
        case 'Claim This Deal':
            subtitle = 'The Ultimate Upgrade For Your Seat Covers';
            comparePrice1 = '';
            diff = '$299+ (Value)';
            break;
        case 'First Row':
            value = 'First Row';
            subtitle = '2 Front Seats';
            if (customCompatibility === 'Y' || customCompatibility === 'F' || customCompatibility === 'YF') {
                value = 'Front Seats';
            }
            break;
        case 'Back Row':
            value = 'Back Row';
            subtitle = '1 Back Bench';
            if (customCompatibility === 'Y' || customCompatibility === 'YF') {
                value = 'Back Bench';
            }
            break;
        case 'Middle Row':
            value = 'Middle Row';
            subtitle = '2 Seats';
            break;
        case 'Front Seats':
            value = 'Front Seats';
            subtitle = '2 Front Seats';
            if (window.scs_black_friday_2025) {
                value = 'Front Seats Only';
            }
            break;
        case 'Universal Back Bench':
        case 'Universal Rear Seats Only':
            // if (label === 'Universal Rear Seats Only') {
            //     break;
            // }
            if (isUniversalBundle(currentAppData)) {
                value = 'Universal Rear Seats Only';
                subtitle = '1 Universal Rear';
                break;
            }
            value = 'Back Bench';
            subtitle = '1 Universal Rear';
            if (isNewRearSeat(currentAppData)) {
                value = 'Rear Seats';
                subtitle = '1 Full Rear Seat';
            }
            if (window.scs_black_friday_2025) {
                value = 'Rear Seats Only';
            }
            break;
        case 'First Row & Back Row':
            value = 'Bundle';
            subtitle = '2 Fronts & 1 Back';
            break;
        case 'Second or Third Row':
            if (customCompatibility === 'Y' || customCompatibility === 'YJ') {
                value = 'Second Row';
                subtitle = 'Rear Seats';
            }
            break;
        case '2 Row Bundle':
            if (customCompatibility === 'Y' || customCompatibility === 'YJ') {
                value = 'Bundle';
                subtitle = '';
            }
            break;
        case 'Bundle':
        case 'Front and Universal Rear Seats':
            // if (label === 'Front and Universal Rear Seats') {
            //     break;
            // }
            // console.log('isUniversalBundle(currentAppData):', isUniversalBundle(currentAppData), currentAppData);   
            if (isUniversalBundle(currentAppData)) {
                value = 'Front and Universal Rear Seats';
                subtitle = '2 Fronts & 1 Universal Rear';
                break;
            }
            value = `Bundle & Save`; //${diff ? '$' + diff : ''}
            if (customCompatibility === 'N') {
                subtitle = '2 Fronts & 1 Back';
            }
            if (customCompatibility === 'Y' || customCompatibility === 'F'
                || customCompatibility === 'YF' || customCompatibility === 'YJ'
                || customCompatibility === 'FJ' || customCompatibility === 'TJ') {
                subtitle = '2 Fronts & 1 Back';
            }
            if (customCompatibility === 'T') {
                subtitle = 'All Rows';
            }
            if (window.scs_ultimate_upgrade) {
                value = 'Front & Back Seats';
                subtitle = 'Bundle & Save (2 fronts & 1 back bench)';
            }
            if (window.scs_black_friday_2025) {
                value = 'Front & Rear Seats';
                
            }
            break;
    }

    return {value, subtitle, diff, diffPercentage, comparePrice: comparePrice1};
}

export const formatSelectedSeat = function (selectedSeat: string | undefined, product: any, compatibility_custom: string, currentAppData = null) {
    let price: string;
    let comparePrice = '';
    const parsed_seat = parseSeatValue(selectedSeat, compatibility_custom);
    if (!product || !product.variants || !product.variants.edges) {
        return '';
    }
    const priceEl = product.variants.edges.filter(v => v.node.selectedOptions
        .filter(er => er.name === 'Seats' && er.value === parsed_seat).length > 0);
    // console.log('priceEl:', priceEl);
    if (priceEl.length > 0) {
        const currency = getCurrency(priceEl[0].node.priceV2.currencyCode);
        price = currency + formatMoney(getOfferPrice(parseFloat(priceEl[0].node.priceV2.amount)));

        if (priceEl[0].node.compareAtPrice) {
            comparePrice = currency + formatMoney(parseFloat(priceEl[0].node.compareAtPrice.amount));
        }

        const {value, subtitle, diff} = getFormattedSeatValue(parsed_seat, price, comparePrice, compatibility_custom, currentAppData);
        return `${value} (${subtitle} - ${price})`;
    }

    return '';
}

export const getImageUrl = function (src, maxWidth, maxHeight) {

    let size = maxWidth + 'x';
    if (maxHeight) {
        size += maxHeight;
    }
    let newSrc = src.replace('.jpg', '_' + size + '.jpg');
    newSrc = newSrc.replace('.png', '_' + size + '.png');
    newSrc = newSrc.replace('.webp', '_' + size + '.webp');
    // format=webp is real content negotiation, not a fixed override — Shopify's
    // CDN honors it based on the request's Accept header (as a real <img> tag
    // sends), not the query param alone. A bare fetch() (no image Accept header)
    // always gets the JPEG fallback here, which is why an earlier test using
    // fetch() wrongly concluded this param was a no-op. Verified in production:
    // same URL, real Accept header -> image/webp at ~18% smaller than the
    // format=pjpg baseline at the same dimensions.
    if (newSrc.indexOf('?') > -1) {
        newSrc = newSrc.replace('?', '?crop=center&format=webp&');
    } else {
        newSrc = newSrc + '?crop=center&format=webp&';
    }

    return newSrc;
}

// Main gallery image was served at one fixed size (546x548, upscaled to
// 1092x1096 via getImageUrl's implicit doubling) to every device regardless
// of viewport/DPI — no srcset existed anywhere in this codebase. Widths
// mirror the theme's own SSR fallback (snippets/scs-pdp-skeleton.liquid:
// widths: '400, 548, 800, 1096').
export const GALLERY_MAIN_WIDTHS = [400, 548, 800, 1096];
export const GALLERY_MAIN_SIZES = '(max-width: 767px) 90vw, 548px';

export const buildMainSrcSet = function (src, maxWidth, maxHeight) {
    if (!src) return '';
    const aspect = maxHeight / maxWidth;
    return GALLERY_MAIN_WIDTHS.map(
        (w) => `${getImageUrl(src, w, Math.round(w * aspect))} ${w}w`
    ).join(', ');
}

export const parseSeatValue = function (seatValue, compatibility_custom) {
    // console.log('compatibility_custom', compatibility_custom);
    let seat = seatValue || '';
    switch (seat) {
        case 'First Row,Back Row':
            seat = 'First Row & Back Row';
            break;
        case 'Back Row,First Row':
            seat = 'First Row & Back Row';
            break;
        case 'Middle Row,Back Row':
            seat = 'Middle Row & Back Row';
            break;
        case 'Back Row,Middle Row':
            seat = 'Middle Row & Back Row';
            break;
        case 'First Row,Middle Row':
            seat = 'First Row & Middle Row';
            break;
        case 'Middle Row,First Row':
            seat = 'First Row & Middle Row';
            break;

        default:
            break;
    }
    return seat;
}

// New rear seat part numbers start with a two-digit prefix under 50 (e.g. 11201);
// this covers the icon configurator and Year/Make/Model/Trim/Cab ordering paths.
export const isNewRearPartNumber = function (partNumber) {
    const prefix = parseInt(String(partNumber ?? '').trim().substring(0, 2), 10);
    return !isNaN(prefix) && prefix < 50;
}

export const isNewRearSeat = function (currentAppData) {
    if (!currentAppData) {
        return false;
    }
    const rearSeat = 'Universal Back Bench';
    const partNumber = getNewPartNumber({...currentAppData, seat: rearSeat});
    if (partNumber) {
        return isNewRearPartNumber(partNumber);
    }
    // No color selected yet — check the rear seat's new_sku mapping for any color
    for (const availability of [currentAppData.sku_mapping_availability, currentAppData.check?.availability]) {
        if (!availability) {
            continue;
        }
        const newSku = availability[resolveAvailabilitySeatKey(rearSeat, availability)]?.['new_sku'];
        if (typeof newSku === 'string' && newSku) {
            return isNewRearPartNumber(newSku);
        }
        if (newSku && typeof newSku === 'object') {
            const firstSku = Object.values(newSku).find(sku => sku);
            if (firstSku) {
                return isNewRearPartNumber(firstSku);
            }
        }
    }
    return false;
}

// Universal bundle part numbers start with a two-digit prefix of 50 or higher;
// mirrors isNewRearPartNumber's < 50 rule for the rear-seat SKU scheme.
export const isUniversalPartNumber = function (partNumber) {
    const prefix = parseInt(String(partNumber ?? '').trim().substring(0, 2), 10);
    return !isNaN(prefix) && prefix >= 50;
}

// A vehicle whose Bundle SKU has a part-number prefix >= 50 uses a universal
// (not vehicle-specific) bundle, so the Bundle/Universal Back Bench options get
// different naming (see getFormattedSeatValue). Mirrors isNewRearSeat exactly,
// but reads the 'Bundle' seat's SKU instead of the rear seat's.
export const isUniversalBundle = function (currentAppData) {
    if (!currentAppData) {
        return false;
    }
    const bundleSeat = 'Bundle';
    const partNumber = getNewPartNumber({...currentAppData, seat: bundleSeat});
    if (partNumber) {
        return isUniversalPartNumber(partNumber);
    }
    // No color selected yet — check the Bundle seat's new_sku mapping for any color
    for (const availability of [currentAppData.sku_mapping_availability, currentAppData.check?.availability]) {
        if (!availability) {
            continue;
        }
        const newSku = availability[resolveAvailabilitySeatKey(bundleSeat, availability)]?.['new_sku'];
        if (typeof newSku === 'string' && newSku) {
            return isUniversalPartNumber(newSku);
        }
        if (newSku && typeof newSku === 'object') {
            const firstSku = Object.values(newSku).find(sku => sku);
            if (firstSku) {
                return isUniversalPartNumber(firstSku);
            }
        }
    }
    return false;
}

// Shopify sends these as distinct option values on universal (see isUniversalBundle)
// vehicles, but internally they're just Bundle / Universal Back Bench under a
// different display name (see getFormattedSeatValue). Every other piece of
// logic keyed off currentAppData.seat — availability/price/variant lookups,
// isBundle(), StepNav's cart property checks, etc. — only knows the canonical
// name, so normalize to it wherever a raw option value becomes `seat`. Display
// already re-derives the friendly name from isUniversalBundle(), not from
// whatever string is stored here, so normalizing is safe for the UI too.
const SEAT_VALUE_ALIASES: Record<string, string> = {
    'Front and Universal Rear Seats': 'Bundle',
    'Universal Rear Seats Only': 'Universal Back Bench',
};

export const normalizeSeatValue = function (seat: string | undefined | null): string {
    if (!seat) return seat ?? '';
    return SEAT_VALUE_ALIASES[seat] ?? seat;
}

export const parsedSeatUserFriendly = function (seatValue, compatibility_custom, currentAppData = null) {
    let seat = seatValue || '';
    console.log('parsedSeatUserFriendly', seatValue, compatibility_custom);
    if (compatibility_custom === 'YF' || compatibility_custom === 'Y') {
        seat = seat.replace('First Row & Back Row', 'Bundle');
    }
    if (compatibility_custom === 'F' || compatibility_custom === 'Y' || compatibility_custom === 'YF') {
        seat = seat.replace('First Row', 'Front Seats');
    }
    if (compatibility_custom === 'Y' || compatibility_custom === 'YF') {
        seat = seat.replace('Back Row', 'Back Bench');
    }
    if (isNewRearSeat(currentAppData)) {
        seat = seat.replace('Universal Back Bench', 'Rear Seats');
    }
    console.log('parsedSeatUserFriendly result', seat);
    return seat;
}

export const isCustom = function (currentAppData) {
    return currentAppData.check && (currentAppData.check.compatibility === 'N'
        && currentAppData.check.compatibility_custom !== 'N');
}

export const isCustomThreeRows = function (currentAppData) {
    return false;
    // return currentAppData.check && (currentAppData.check.compatibility === 'N'
    //     && currentAppData.check.compatibility_custom === 'T');
}

export const isCustomFront = function (currentAppData) {
    return currentAppData.check && (
        (currentAppData.check.compatibility_custom === 'F'
            || currentAppData.check.compatibility_custom === 'FJ'));
}

export const isCustomFrontJump = function (currentAppData) {
    return currentAppData.check && (
        window.scs_force_normal !== true &&
        (currentAppData.check.compatibility_custom === 'YF'
            || currentAppData.check.compatibility_custom === 'FJ')
    );
}

export const isCustomJump = function (currentAppData) {
    return currentAppData.check && (currentAppData.check.compatibility === 'N'
        &&
        (currentAppData.check.compatibility_custom === 'YJ'
            || currentAppData.check.compatibility_custom === 'FJ'
            || currentAppData.check.compatibility_custom === 'TJ'
            || currentAppData.check.compatibility_custom === 'YF'
        )
    );
}

export const formatMoney = function (money) {
    return window.scs_force_custom ? money.toFixed(2) : money.toFixed(0);
}

// The Jump Seat Cover addon (see Step2.tsx/Step3New.tsx/StepNav.tsx) is a
// separate Shopify product from the main seat cover, keyed by color rather
// than by a variant lookup on the main product — this is the one place that
// mapping is defined, so cart-add (StepNav) and price display (Step2New/
// Step3New) can't drift out of sync.
export const getJumpSeatCoverId = (colorId: string | undefined | null): number => {
    const colorToJumpSeatCover: Record<string, number> = {
        Black: 49988033872164,
        Brown: 49988033904932,
        Grey: 49988033937700,
        Beige: 49988033970468,
        "Wine Red": 49988034003236,
    };

    return typeof colorId === "string" && typeof colorToJumpSeatCover[colorId] !== "undefined"
        ? colorToJumpSeatCover[colorId]
        : colorToJumpSeatCover["Black"];
};

/**
 * Format a price amount using Shopify's active currency rate and money format.
 * Falls back gracefully when Shopify globals are not present.
 *
 * @param amount - The raw price amount in the shop's base currency (e.g. 149.99)
 * @returns Formatted price string with currency symbol (e.g. "$150" or "CA$210.00")
 */
export const formatShopifyMoney = function (amount: number): string {
    const rate: number = (window as any).Shopify?.currency?.rate
        ? parseFloat((window as any).Shopify.currency.rate)
        : 1;

    const converted = amount * rate;

    // Shopify money_format strings use placeholders like {{amount}}, {{amount_no_decimals}}, etc.
    const moneyFormat: string =
        (window as any).Shopify?.money_format ?? '${{amount}}';

    // Determine the placeholder being used and format accordingly
    if (moneyFormat.includes('{{amount_no_decimals_with_comma_separator}}')) {
        const formatted = Math.round(converted)
            .toFixed(0)
            .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
            .replace(/\.(\d{2})$/, ',$1');
        return moneyFormat.replace('{{amount_no_decimals_with_comma_separator}}', formatted);
    }
    if (moneyFormat.includes('{{amount_with_comma_separator}}')) {
        const formatted = converted
            .toFixed(2)
            .replace('.', ',')
            .replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return moneyFormat.replace('{{amount_with_comma_separator}}', formatted);
    }
    if (moneyFormat.includes('{{amount_no_decimals}}')) {
        return moneyFormat.replace('{{amount_no_decimals}}', Math.round(converted).toFixed(0));
    }
    // Default: {{amount}} – 2 decimal places, but drop trailing .00 for cleaner display
    const formatted = window.scs_force_custom
        ? converted.toFixed(2)
        : Math.round(converted).toFixed(0);
    return moneyFormat.replace('{{amount}}', formatted);
};

export const isRear = function (currentAppData) {
    return window.scs_show_bonus_section && currentAppData?.seat && (currentAppData?.seat?.indexOf('Bundle') !== -1 || currentAppData?.seat?.indexOf('Back') !== -1 || currentAppData?.seat?.indexOf('Front') !== -1);
}

export const isFront = function (currentAppData) {
    return window.scs_show_bonus_section && currentAppData?.seat && (currentAppData?.seat?.indexOf('Bundle') !== -1 || currentAppData?.seat?.indexOf('Front') !== -1);
}

export const isBundle = function (currentAppData) {
    return window.scs_show_bonus_section && currentAppData?.seat && (currentAppData?.seat?.indexOf('Bundle') !== -1);
}

export const getOfferPrice = function (price: number) {
    if (window.scs_show_earlybf_section || window.scs_father_day_2026) {
        return price ; //- 20;
    }
    return price;
}

export const getCompareOfferPrice = function (price: number, compare_price?: number) {
    if (window.scs_show_earlybf_section) {
        //console.debug('compare_price: ', compare_price, 'price: ', price);
        if (compare_price !== undefined) {
            return compare_price;
        }
        return price;
    }
    return compare_price;
}

export const getPriceElemById = function (variants, selectedId, currentAppData?: any) {
    const filtered = variants && variants.edges.length > 0
                ? variants.edges.filter(
                    (el) =>
                        el.node.id.replace("gid://shopify/ProductVariant/", "") ===
                        "" + selectedId,
                )
                : [];
        //console.log("filtered", filtered);
    if (filtered.length == 0) {
        return [];
    }

    const seatForPriceTest = filtered[0]?.node?.selectedOptions.filter(er => er.name === 'Seats')[0]?.value;

    if (window.scs_black_friday_2025) {
        if (!filtered[0].node.compareAtPrice) {
            filtered[0].node.compareAtPrice = {
                amount: 0,
                currency: 'USD',
            };
        }

        switch (filtered[0]?.node?.selectedOptions
            .filter(er => er.name === 'Seats')[0].value) {
            case 'Bundle':
            case 'Front and Universal Rear Seats':
                filtered[0].node.compareAtPrice.amount = cleanPrice(window.scs_promo_bonus_compare_price_1);//'783';//788.98
                filtered[0].node.priceV2.amount = cleanPrice(window.scs_promo_bonus_sale_price_1);//'369';
                break;
            case 'Universal Back Bench':
            case 'Universal Rear Seats Only':
                filtered[0].node.compareAtPrice.amount = cleanPrice(window.scs_promo_bonus_compare_price_2);//'504';//509.98
                filtered[0].node.priceV2.amount = cleanPrice(window.scs_promo_bonus_sale_price_2);//'259';
                break;
            case 'Front Seats':
                filtered[0].node.compareAtPrice.amount = cleanPrice(window.scs_promo_bonus_compare_price_3);//'504';//509.98
                filtered[0].node.priceV2.amount = cleanPrice(window.scs_promo_bonus_sale_price_3);//'259';
                break;

        }
    }

    // Runs last so scs_price_test_26 always wins over the black-friday-style promo
    // override above, rather than being silently clobbered back to the promo price.
    applyPriceTest26CoverageOverride(filtered[0].node, seatForPriceTest, currentAppData);

    return filtered;
}

export const getVariantMetafields = function (variants, selectedId) {
    const filtered = variants && variants.edges.length > 0
        ? variants.edges.filter(
            (el) =>
                el.node.id.replace("gid://shopify/ProductVariant/", "") ===
                "" + selectedId,
        )
        : [];
    if (filtered.length == 0) {
        return null;
    }

    const preorder_enabled = filtered[0].node.preorder_enabled?.value; 
    const preorder_ship_date = filtered[0].node.preorder_ship_date?.value;

    return {
        preorder_enabled: preorder_enabled,
        preorder_ship_date: preorder_ship_date
    };
}

export const getPriceElem = function (variants, name1, currentAppData?: any) {
    const priceEl = variants.edges.filter(v => v.node.selectedOptions
        .filter(er => er.name === 'Seats' && er.value === name1).length > 0);
    if (priceEl.length == 0) {
        return [];
    }

    if (window.scs_black_friday_2025) {
        if (!priceEl[0].node.compareAtPrice) {
            priceEl[0].node.compareAtPrice = {
                amount: 0,
                currency: 'USD',
            };
        }
        switch (name1) {
            case 'Bundle':
            case 'Front and Universal Rear Seats':
                priceEl[0].node.compareAtPrice.amount = cleanPrice(window.scs_promo_bonus_compare_price_1);//788.99
                priceEl[0].node.priceV2.amount = cleanPrice(window.scs_promo_bonus_sale_price_1);
                break;
            case 'Universal Back Bench':
            case 'Universal Rear Seats Only':
                priceEl[0].node.compareAtPrice.amount = cleanPrice(window.scs_promo_bonus_compare_price_2);//509.98
                priceEl[0].node.priceV2.amount = cleanPrice(window.scs_promo_bonus_sale_price_2);
                break;
            case 'Front Seats':
                priceEl[0].node.compareAtPrice.amount = cleanPrice(window.scs_promo_bonus_compare_price_3);//509.98
                priceEl[0].node.priceV2.amount = cleanPrice(window.scs_promo_bonus_sale_price_3);
                break;

        }
    }

    // Runs last so scs_price_test_26 always wins over the black-friday-style promo
    // override above, rather than being silently clobbered back to the promo price.
    applyPriceTest26CoverageOverride(priceEl[0].node, name1, currentAppData);
    return priceEl;
}

const cleanPrice = (price) => {
    const price1 = (price || '').toString().replace(/[$€#A-Za-z]/g, '').trim();
    //console.log('price1: ', price1);
    return price1;
}

export const getNewPartNumber = (currentAppData: {
    step?: number | undefined;
    seat?: string | undefined;
    color?: string | undefined;
    selectedId?: string | undefined;
    year?: number | undefined;
    make?: string | undefined;
    model?: string | undefined;
    check?: { compatibility: string; compatibility_custom: string; availability: object } | undefined;
    product?: ProductType | undefined;
    originalProduct?: ProductType | undefined;
    addons?: string | undefined;
    trim?: string | undefined;
    cab?: string | undefined;
    has_trims?: boolean | undefined;
    has_cabs?: boolean | undefined;
    with_bonus?: boolean | undefined;
    trims?: object | undefined | null;
    sku_mapping_availability?: Record<string, any> | null;
}) => {
    if (!currentAppData?.seat || !currentAppData?.color) {
        return null;
    }

    const seatKeyForSku = resolveAvailabilitySeatKey(currentAppData.seat, currentAppData.sku_mapping_availability ?? undefined);
    const skuMapNew = currentAppData.sku_mapping_availability?.[seatKeyForSku]?.['new_sku']?.[currentAppData.color];
    if (skuMapNew !== undefined && skuMapNew !== null) {
        return skuMapNew;
    }

    const frontSeatsNewSku = currentAppData.sku_mapping_availability?.["Front Seats"]?.["new_sku"];
    if (typeof frontSeatsNewSku === 'string' && frontSeatsNewSku) {
        return frontSeatsNewSku;
    }

    if (!currentAppData?.check) {
        return null;
    }
    const seatKeyForCheck = resolveAvailabilitySeatKey(currentAppData.seat, currentAppData?.check?.availability ?? undefined);
    if (!currentAppData?.check?.availability || !currentAppData?.check?.availability[seatKeyForCheck]) {
        return null;
    }
    if (!currentAppData?.check?.availability[seatKeyForCheck]['new_sku']) {
        return null;
    }
    if (typeof currentAppData?.check?.availability[seatKeyForCheck]['new_sku'][currentAppData.color] === 'undefined') {
        return null;
    }
    return currentAppData?.check?.availability[seatKeyForCheck]['new_sku'][currentAppData.color];
}

// Looks up the part number for a seat+"Black" combo, for the sole purpose of the
// scs_price_test_26 lookahead below. Deliberately NOT reusing getNewPartNumber: its
// "Front Seats" flat-string fallback exists to pick a real order SKU when nothing else
// is available, and it ignores the seat being asked about — exactly wrong for a
// lookahead into a seat the shopper hasn't actually selected yet, since it can return a
// different seat's part number and make the override silently no-op or misclassify.
function getPriceTest26LookaheadPartNumber(currentAppData: any, seat: string): string | null {
    const seatKeyForSku = resolveAvailabilitySeatKey(seat, currentAppData?.sku_mapping_availability ?? undefined);
    const skuMapNew = currentAppData?.sku_mapping_availability?.[seatKeyForSku]?.['new_sku']?.['Black'];
    if (skuMapNew !== undefined && skuMapNew !== null) {
        return skuMapNew;
    }

    const seatKeyForCheck = resolveAvailabilitySeatKey(seat, currentAppData?.check?.availability ?? undefined);
    const checkNewSku = currentAppData?.check?.availability?.[seatKeyForCheck]?.['new_sku']?.['Black'];
    return typeof checkNewSku === 'undefined' ? null : checkNewSku;
}

// Coverage type is read from the 3rd digit of the seat's own part number, rather than
// trusting the seat name string — display names vary by vehicle (Bundle vs Set vs
// Front & Back Bundle, Universal Back Bench vs Rear Seats, etc.) but this digit is
// structural and consistent:
//   1 -> Front Seats
//   2 -> Back Bench / Universal Back Bench / Rear Seats / Universal Rear Seats Only
//   3 -> Set / Bundle / Front & Back Bundle / Front and Universal Rear Seats
// Front and Rear get overridden to $289 (was $428, i.e. a $139 saving). Bundle/Set
// (digit 3) keeps its normal Shopify price — no override for its priceV2 — but its
// compareAtPrice ("was") is anchored to a fixed $717 (289 + 289 + 139) so the savings
// badge stays consistent with the Front/Rear test prices: e.g. a $389 Bundle shows
// "Save $328 Today".
const PRICE_TEST_26_FRONT_REAR_NOW = '289';
const PRICE_TEST_26_FRONT_REAR_WAS = '428';
const PRICE_TEST_26_BUNDLE_WAS = '717';

function setCompareAtAmount(node: any, amount: string) {
    if (!node.compareAtPrice) {
        node.compareAtPrice = { amount: 0, currency: node.priceV2?.currencyCode ?? 'USD' };
    }
    node.compareAtPrice.amount = amount;
}

// Overrides a variant node's price under the scs_price_test_26 flag. The part number is
// looked up for "Black" regardless of the shopper's actual color choice (or lack of
// one) — the coverage-type digit is identical across colors, so this lookahead is
// always correct and lets prices update before a color has even been selected.
export function applyPriceTest26CoverageOverride(node: any, seat: string | undefined, currentAppData: any) {
    if (!window.scs_price_test_26 || !seat) {
        return;
    }
    // Seat-option buttons pass the raw Shopify option value ("Front and Universal Rear
    // Seats" on universal vehicles); normalize so the lookahead resolves the same
    // availability/sku_mapping key as the post-selection price does.
    const normalizedSeat = normalizeSeatValue(seat);
    const partNumber = getPriceTest26LookaheadPartNumber(currentAppData, normalizedSeat);
    const coverageDigit = partNumber ? String(partNumber).charAt(2) : '';

    if (coverageDigit === '1' || coverageDigit === '2') {
        node.priceV2.amount = PRICE_TEST_26_FRONT_REAR_NOW;
        setCompareAtAmount(node, PRICE_TEST_26_FRONT_REAR_WAS);
        return;
    }

    if (coverageDigit === '3') {
        setCompareAtAmount(node, PRICE_TEST_26_BUNDLE_WAS);
    }
}

// Mirrors the `_components` resolution used for the add-to-cart analytics
// property in StepNav.tsx — same seat/color lookup, first against
// check.availability, then overridden by sku_mapping_availability's prekit
// components if present. Kept in one place so ship-by-date and analytics
// never disagree about which SKUs make up a seat+color combo.
export const getComponentSkus = (currentAppData: {
    seat?: string | undefined;
    color?: string | undefined;
    check?: { availability?: Record<string, any> } | undefined;
    sku_mapping_availability?: Record<string, any> | null;
}): string[] => {
    if (!currentAppData?.seat || !currentAppData?.color) {
        return [];
    }

    let components = currentAppData.check?.availability?.[currentAppData.seat]?.components?.[currentAppData.color];

    const skuMapSeatKey = resolveAvailabilitySeatKey(currentAppData.seat, currentAppData.sku_mapping_availability ?? undefined);
    const skuMapComponents = currentAppData.sku_mapping_availability?.[skuMapSeatKey]?.prekit?.[currentAppData.color]?.components;
    if (skuMapComponents !== undefined && skuMapComponents !== null) {
        components = skuMapComponents;
    }

    if (!components) return [];
    const list = Array.isArray(components) ? components : [components];
    // Component SKUs are often stored as a single comma-joined string
    // (e.g. "BLF+BL-ULHR-2,BL-CM,BL-ULHR-2,BL-UXSHR") — split those out so
    // each individual SKU can be matched against scs_off_site_skus_full.
    return list.flatMap((c) => String(c).split(',').map((s) => s.trim()).filter(Boolean));
};

export function resolveAvailabilitySeatKey(seat: string, availability: Record<string, any> | null | undefined): string {
    if (!seat) return seat;
    if (!availability) return seat;
    // Canonical aliases mapping: lookup synonyms for common seat keys
    const SEAT_KEY_ALIASES: Record<string, string[]> = {
        'Bundle': ['Set', 'Front and Universal Rear Seats'],
        'Set': ['Bundle'],
        'Front and Universal Rear Seats': ['Bundle', 'Set'],
        'Universal Back Bench': ['Rear Seats', 'Universal Rear Seats Only'],
        'Universal Rear Seats Only': ['Universal Back Bench', 'Rear Seats'],
        'Back Bench': ['Rear Seats'],
        'Rear Bench': ['Rear Seats'],
        'Rear Seat': ['Rear Seats'],
    };
    if (availability[seat] !== undefined) return seat;
    for (const alias of (SEAT_KEY_ALIASES[seat] ?? [])) {
        if (availability[alias] !== undefined) return alias;
    }
    // Also try reverse: if seat doesn't exist, check if any canonical maps to seat
    for (const [canonical, aliases] of Object.entries(SEAT_KEY_ALIASES)) {
        if (aliases.includes(seat) && availability[canonical] !== undefined) return canonical;
    }
    return seat;
}

// Mirrors the availability check StepNav uses to auto-advance the stepper:
// true when at least one seat/color combination in `check.availability` is available.
export function hasAvailableSeat(check: { availability?: Record<string, any> } | null | undefined): boolean {
    const availability = check?.availability;
    if (!availability) return false;
    return Object.values(availability).some((seatObj) => {
        try {
            return Object.values(seatObj as Record<string, any>).some((v) => v === true || v === 'true');
        } catch {
            return false;
        }
    });
}

// Loosely matches a "?setup=front|rear|bundle" URL hint against a raw Shopify
// seat option name (e.g. "Front Seats", "Universal Back Bench", "Bundle") so a
// deep link can nudge the auto-select fallback without needing to know the
// exact option string for every vehicle.
export function matchesSetupHint(seatName: string, hint: string): boolean {
    if (!seatName || !hint) return false;
    const name = seatName.toLowerCase();
    const hasFront = name.includes('front') || name.includes('first row');
    const hasRear = name.includes('rear') || name.includes('back') || name.includes('bench');
    switch (hint) {
        case 'front':
            return hasFront && !hasRear;
        case 'rear':
            return hasRear && !hasFront;
        case 'bundle':
            return name.includes('bundle') || name.includes('set') || (hasFront && hasRear);
        default:
            return false;
    }
}

// Manual overrides: prekit SKUs that should be treated as out of stock (hidden)
const OUT_OF_STOCK_PREKIT_SKU_OVERRIDES: string[] = [
    // 'BG-Ford 2',
    // 'BG-Ford 2-V1',
    // 'BG-Ford 2-V2',
    // 'BG-Ford 2-Rear',
    // 'BG-Ford 2-Rear-V1',
    // 'BG-Ford 2-Rear-V2',
    // 'BG-Ford 2B',
    // 'BG-Ford 2B-V1',
    // 'BG-Ford 2B-V2',
    // 'BG-Ford 2B-Rear',
    // 'BG-Ford 2B-Rear-V1',
    // 'BG-Ford 2B-Rear-V2',
    // 'BG-Ford 2C',
    // 'BG-Ford 2C-V1',
    // 'BG-Ford 2C-V2',
    // 'BG-Ford 2C-Rear',
    // 'BG-Ford 2C-Rear-V1',
    // 'BG-Ford 2C-Rear-V2',
    // 'BB+BB-ULHR-2', // 2024 Audi A4 - Black & Blue out of stock
    // 'WR-CM',
];

// Merchant-managed overrides (Site Availability page in the app) come in via
// window.scs_off_site_skus, set by the scs-off-site-skus theme app embed from
// a shop metafield. Merged with the hardcoded list above so existing entries
// keep working even before the embed is enabled.
export function getOffSiteSkus(): string[] {
    const runtime = (typeof window !== 'undefined' && Array.isArray((window as any).scs_off_site_skus))
        ? (window as any).scs_off_site_skus as string[]
        : [];
    return [...OUT_OF_STOCK_PREKIT_SKU_OVERRIDES, ...runtime];
}

// Prekit/bundle components come as an array or a comma-separated string,
// optionally with a quantity prefix (e.g. "2xWR-CM").
type SkuComponentList = string | string[] | null | undefined;

// Normalizes a SkuComponentList to a flat list of bare SKUs.
// Array elements may themselves be comma-separated (as returned by the biz2 API
// for prekit components), so each element is also split before stripping prefixes.
function normalizeSkuList(components: SkuComponentList): string[] {
    if (!components) return [];
    const list = Array.isArray(components)
        ? components.flatMap((c) => String(c).split(','))
        : components.split(',');
    return list.map((c) => String(c).trim().replace(/^\d+x/i, '')).filter(Boolean);
}

// The component SKUs already included in the currently selected seat/color
// bundle (prekit), e.g. for a Wine Red bundle this would include 'WR-CM'.
// Mirrors the `_components` lookup used for the analytics property in StepNav.
export const getSelectedBundleComponentSkus = (currentAppData: {
    seat?: string;
    color?: string;
    check?: { availability?: Record<string, any> } | null;
    sku_mapping_availability?: Record<string, any> | null;
}): string[] => {
    const seat = currentAppData?.seat;
    const color = currentAppData?.color;
    if (!seat || !color) {
        return [];
    }

    let components: SkuComponentList;

    const checkAvailability = currentAppData?.check?.availability;
    if (checkAvailability) {
        const seatKey = resolveAvailabilitySeatKey(seat, checkAvailability);
        components = checkAvailability[seatKey]?.components?.[color];
    }

    const skuMapAvailability = currentAppData?.sku_mapping_availability;
    if (skuMapAvailability) {
        const seatKey = resolveAvailabilitySeatKey(seat, skuMapAvailability);
        const skuMapComponents = skuMapAvailability[seatKey]?.prekit?.[color]?.components;
        if (skuMapComponents !== undefined && skuMapComponents !== null) {
            components = skuMapComponents;
        }
    }

    return normalizeSkuList(components);
};

// SKUs that should not be offered as a separate vehicle addon: anything
// already baked into the selected bundle's components, plus anything
// manually flagged out-of-stock/off-site.
export const getExcludedAddonSkus = (currentAppData: {
    seat?: string;
    color?: string;
    check?: { availability?: Record<string, any> } | null;
    sku_mapping_availability?: Record<string, any> | null;
}): string[] => {
    return Array.from(new Set([
        ...getSelectedBundleComponentSkus(currentAppData),
        ...getOffSiteSkus(),
    ]));
};

// True when any prekit component SKU is in the out-of-stock overrides list.
export function hasOutOfStockComponent(components: SkuComponentList): boolean {
    const offSiteSkus = getOffSiteSkus();
    return normalizeSkuList(components).some((component) => offSiteSkus.includes(component));
}

// Hides a color whose sku-mapping prekit SKU — or any of its component SKUs —
// is in the out-of-stock overrides list (e.g. WR-CM hides Wine Red), or whose
// availability is explicitly null.
export function isColorHiddenBySkuMappingAvailability(currentAppData: any, seat: string, color: string): boolean {
    const availability = currentAppData?.sku_mapping_availability;
    if (!availability || !seat || !color) {
        return false;
    }

    const seatKey = resolveAvailabilitySeatKey(seat, availability);
    const seatAvailability = availability[seatKey];
    if (!seatAvailability || typeof seatAvailability !== 'object') {
        return availability[color] === null;
    }

    const prekit = Array.isArray(seatAvailability.prekit) ? undefined : seatAvailability.prekit;

    if (prekit?.[color]?.sku && getOffSiteSkus().includes(prekit[color].sku)) {
        return true;
    }
    if (hasOutOfStockComponent(prekit?.[color]?.components)) {
        return true;
    }

    return seatAvailability[color] === null || prekit?.[color] === null;
}

// Same override applied to the check-availability path (vehicles without a
// configurator, e.g. 2024 Toyota RAV4): components live at
// availability[seat].components[color] as a comma-separated string.
export function isColorHiddenByCheckAvailability(currentAppData: any, seat: string, color: string): boolean {
    const availability = currentAppData?.check?.availability;
    if (!availability || !seat || !color) {
        return false;
    }
    const seatAvailability = availability[resolveAvailabilitySeatKey(seat, availability)];
    if (!seatAvailability || typeof seatAvailability !== 'object') {
        return false;
    }
    const prekit = seatAvailability.prekit?.[color];
    if (prekit?.sku) {
        if (!prekit.virtual) {
            // Real Shopify variant — check its SKU directly.
            if (getOffSiteSkus().includes(prekit.sku)) return true;
        } else {
            // Virtual product: the check-API sku is a template code (e.g. "BR+BR-ULHR-2"),
            // not the real variant. Find the actual virtual-bundle SKU from sku_mapping_availability.
            const skuMapAvail = currentAppData?.sku_mapping_availability;
            if (skuMapAvail) {
                const realSku = skuMapAvail[resolveAvailabilitySeatKey(seat, skuMapAvail)]?.prekit?.[color]?.sku;
                if (realSku && getOffSiteSkus().includes(realSku)) return true;
            }
        }
    }
    return hasOutOfStockComponent(seatAvailability.components?.[color]);
}


export const MODEL_SORT_ORDER: Record<string, Record<string, number>> = {
    Ford: {
        'F-150': 9010,
        'F-250': 9009,
        'F-350': 9008,
        'Ranger': 9007,
    },
    Ram: {
        '1500': 8510,
        '2500': 8509,
        '3500': 8508,
    },
    Chevrolet: {
        'Silverado 1500': 8010,
        'Silverado 2500 HD': 8009,
        'Silverado 3500 HD': 8008,
        'Colorado': 8007,
    },
    GMC: {
        'Sierra 1500': 7510,
        'Sierra 2500 HD': 7509,
        'Sierra 3500 HD': 7508,
        'Canyon': 7507,
    },
    Dodge: {
        'Ram 1500': 7010,
        'Ram 2500': 7009,
        'Ram 3500': 7008,
    },
    Toyota: {
        'Tundra': 6510,
        'Tacoma': 6509,
        '4Runner': 6508,
    },
}

export const getTrims = (trims, model) => {
    if (trims == null || typeof trims === 'string') {
        return [];
    }
    if (typeof trims[model] === "undefined") {
        return [];
    }
    return [
        ...new Set(
            trims[model].map((item) => item.trim).filter((item) => item != ""),
        ),
    ].sort();
}

export const isCustomRearAvailable = function (currentAppData) {
    if (!window.scs_custom_rear_seats) return false;
    const compat = currentAppData?.check?.compatibility;
    // Use the unforced value so this add-on keeps working even when
    // scs_force_normal suppresses compatibility_custom for the main flow.
    const customCompat = currentAppData?.check?.compatibility_custom_raw ?? currentAppData?.check?.compatibility_custom;
    const isFrontsOnly = compat === 'F' || compat === 'HF' || compat === 'AF' || compat === 'BF';
    return isFrontsOnly && customCompat && customCompat !== 'N';
}

export const getCustomRearOptions = function (customCompatibility) {
    if (customCompatibility === 'T' || customCompatibility === 'TJ') {
        return [
            { key: '2nd_row', label: 'Custom 2nd Row', price: 279, msrp: 458 },
            { key: '3rd_row', label: 'Custom 3rd Row', price: 279, msrp: 458 },
        ];
    }
    return [
        { key: 'rear_seat', label: 'Custom 2nd Row', price: 279, msrp: 458 },
    ];
}

export const handleColorChange = (currentAppData, newColor, id, setCurrentAppData) => {
        let id1 = id;
        const parsed_seat = parseSeatValue(currentAppData.seat, currentAppData?.check?.compatibility_custom);
        if (parsed_seat !== currentAppData.seat) {
            // find variant id for this specific parsed_seat
            // console.log('seat and color: ', parsed_seat, newColor);
            const seats_filtered = currentAppData.product.variants.edges.filter(v =>
                v.node.selectedOptions.filter(ep => ep.name === 'Seats' && ep.value === parsed_seat).length > 0);

            // console.log('seats_filtered: ', seats_filtered);
            const filtered_by_color = seats_filtered
                .filter(v => v.node.selectedOptions.filter(ep => ep.name === 'Color' && ep.value === newColor).length > 0);

            // console.log('filtered_by_color: ', filtered_by_color);
            if (filtered_by_color.length > 0)
                id1 = filtered_by_color[0].node?.id?.replace('gid://shopify/ProductVariant/', '');
            // console.log('selected id filtered: ' + id);
        }

        setCurrentAppData(prevState => {
            return { ...prevState, selectedId: id1 }
        })
        return id1;
    }